<script>
import Layout from "../../layouts/main";
import PageHeader from "../../components/page-header";

const GROUPS = [
    { key: "pregrado", label: "Pregrado", icon: "bx-book-open" },
    { key: "postgrado", label: "Postgrado", icon: "bx-award" },
    { key: "advance", label: "Advance", icon: "bx-rocket" }
];

export default {
    components: { Layout, PageHeader },
    data() {
        return {
            title: "Aranceles HubSpot",
            items: [
                { text: "Dashboard", href: "/" },
                { text: "Aranceles HubSpot", active: true }
            ],
            groups: GROUPS,
            activeGroup: "pregrado",
            products: [],
            summary: {
                total: 0,
                visible: 0,
                hidden: 0,
                by_group: {}
            },
            lastSyncAt: null,
            loading: false,
            syncing: false,
            saving: [],
            error: "",
            success: "",
            filters: {
                search: "",
                visibility: "all"
            }
        };
    },
    computed: {
        filteredProducts() {
            const search = this.normalize(this.filters.search);

            return this.products.filter(product => {
                if (product.grupo !== this.activeGroup) {
                    return false;
                }

                if (this.filters.visibility === "visible" && !product.visible) {
                    return false;
                }

                if (this.filters.visibility === "hidden" && product.visible) {
                    return false;
                }

                return !search || this.normalize(product.nombre).includes(search);
            });
        }
    },
    mounted() {
        this.loadProducts();
    },
    methods: {
        async loadProducts() {
            this.loading = true;
            this.error = "";

            try {
                const response = await axios.get("/api/hubspot/products");
                this.products = response.data.products || [];
                this.summary = response.data.summary || this.summary;
                this.lastSyncAt = response.data.last_sync_at || null;
            } catch (error) {
                this.error = this.errorMessage(error, "No fue posible cargar los aranceles.");
            } finally {
                this.loading = false;
            }
        },
        async syncProducts() {
            this.syncing = true;
            this.error = "";
            this.success = "";

            try {
                const response = await axios.post("/api/hubspot/products/sync");
                await this.loadProducts();
                this.success = response.data.message || "Aranceles actualizados desde HubSpot.";
                this.$toasted.success(this.success);
            } catch (error) {
                this.error = this.errorMessage(error, "No fue posible actualizar desde HubSpot.");
                this.$toasted.error(this.error);
            } finally {
                this.syncing = false;
            }
        },
        async updateProduct(product, changes) {
            if (this.saving.includes(product.id)) {
                return;
            }

            const previous = {
                visible: product.visible,
                grupo: product.grupo
            };
            Object.assign(product, changes);
            this.saving.push(product.id);
            this.error = "";

            try {
                const response = await axios.put(`/api/hubspot/products/${product.id}`, changes);
                Object.assign(product, response.data.product);
                this.recalculateSummary();
            } catch (error) {
                Object.assign(product, previous);
                this.error = this.errorMessage(error, "No fue posible guardar el cambio.");
                this.$toasted.error(this.error);
            } finally {
                this.saving = this.saving.filter(id => id !== product.id);
            }
        },
        toggleVisibility(product) {
            this.updateProduct(product, { visible: !product.visible });
        },
        changeGroup(product, event) {
            this.updateProduct(product, { grupo: event.target.value });
        },
        recalculateSummary() {
            this.summary = {
                total: this.products.length,
                visible: this.products.filter(product => product.visible).length,
                hidden: this.products.filter(product => !product.visible).length,
                by_group: GROUPS.reduce((counts, group) => {
                    counts[group.key] = this.products.filter(product => product.grupo === group.key).length;
                    return counts;
                }, {})
            };
        },
        groupCount(group) {
            return Number((this.summary.by_group || {})[group] || 0);
        },
        isSaving(product) {
            return this.saving.includes(product.id);
        },
        formatCurrency(value) {
            return new Intl.NumberFormat("es-CL", {
                style: "currency",
                currency: "CLP",
                maximumFractionDigits: 0
            }).format(Number(value || 0));
        },
        formatDate(value) {
            if (!value) {
                return "Sin actualizar";
            }

            return new Intl.DateTimeFormat("es-CL", {
                dateStyle: "medium",
                timeStyle: "short"
            }).format(new Date(value));
        },
        normalize(value) {
            return String(value || "")
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase();
        },
        errorMessage(error, fallback) {
            if (error.response && error.response.data) {
                return error.response.data.message || fallback;
            }

            return fallback;
        }
    }
};
</script>

<template>
    <Layout>
        <div class="container-fluid aranceles-page">
            <PageHeader :title="title" :items="items" />

            <div class="hero-card mb-4">
                <div>
                    <div class="eyebrow">Catálogo para el simulador</div>
                    <h2>Programas y aranceles</h2>
                    <p class="mb-0">
                        Administra qué carreras estarán disponibles en el simulador público.
                        Los cambios de visibilidad se guardan localmente.
                    </p>
                </div>
                <div class="sync-area">
                    <small>Última actualización</small>
                    <strong>{{ formatDate(lastSyncAt) }}</strong>
                    <button class="btn btn-light btn-lg" :disabled="syncing" @click="syncProducts">
                        <i :class="syncing ? 'bx bx-loader-alt bx-spin' : 'bx bx-refresh'"></i>
                        {{ syncing ? "Actualizando..." : "Actualizar aranceles desde HubSpot" }}
                    </button>
                </div>
            </div>

            <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
                <i class="bx bx-error-circle mr-2"></i>{{ error }}
                <button type="button" class="close" @click="error = ''"><span>&times;</span></button>
            </div>
            <div v-if="success" class="alert alert-success alert-dismissible fade show" role="alert">
                <i class="bx bx-check-circle mr-2"></i>{{ success }}
                <button type="button" class="close" @click="success = ''"><span>&times;</span></button>
            </div>

            <div class="row mb-4">
                <div class="col-md-4 mb-3 mb-md-0">
                    <div class="metric-card">
                        <div class="metric-icon bg-soft-primary text-primary"><i class="bx bx-list-ul"></i></div>
                        <div><span>Total catálogo</span><strong>{{ summary.total }}</strong></div>
                    </div>
                </div>
                <div class="col-md-4 mb-3 mb-md-0">
                    <div class="metric-card">
                        <div class="metric-icon bg-soft-success text-success"><i class="bx bx-show"></i></div>
                        <div><span>Visibles en simulador</span><strong>{{ summary.visible }}</strong></div>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="metric-card">
                        <div class="metric-icon bg-soft-secondary text-secondary"><i class="bx bx-hide"></i></div>
                        <div><span>Ocultos</span><strong>{{ summary.hidden }}</strong></div>
                    </div>
                </div>
            </div>

            <div class="card catalog-card">
                <div class="card-body">
                    <div class="group-tabs mb-4">
                        <button
                            v-for="group in groups"
                            :key="group.key"
                            type="button"
                            :class="['group-tab', { active: activeGroup === group.key }]"
                            @click="activeGroup = group.key"
                        >
                            <i :class="['bx', group.icon]"></i>
                            <span>{{ group.label }}</span>
                            <b-badge :variant="activeGroup === group.key ? 'light' : 'secondary'">
                                {{ groupCount(group.key) }}
                            </b-badge>
                        </button>
                    </div>

                    <div class="row align-items-center mb-3">
                        <div class="col-lg-7 mb-2 mb-lg-0">
                            <div class="search-box">
                                <i class="bx bx-search-alt"></i>
                                <input v-model="filters.search" type="search" class="form-control" placeholder="Buscar carrera o programa..." />
                            </div>
                        </div>
                        <div class="col-lg-5">
                            <select v-model="filters.visibility" class="form-control">
                                <option value="all">Todos los estados</option>
                                <option value="visible">Solo visibles</option>
                                <option value="hidden">Solo ocultos</option>
                            </select>
                        </div>
                    </div>

                    <div v-if="loading" class="empty-state">
                        <i class="bx bx-loader-alt bx-spin"></i>
                        <p>Cargando catálogo...</p>
                    </div>

                    <div v-else-if="!products.length" class="empty-state">
                        <i class="bx bx-cloud-download"></i>
                        <h5>Aún no hay aranceles sincronizados</h5>
                        <p>Usa el botón superior para importar el catálogo desde HubSpot.</p>
                    </div>

                    <div v-else-if="!filteredProducts.length" class="empty-state compact">
                        <i class="bx bx-search"></i>
                        <p>No hay programas que coincidan con los filtros.</p>
                    </div>

                    <div v-else class="table-responsive">
                        <table class="table table-hover align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Programa</th>
                                    <th>Clasificación HubSpot</th>
                                    <th>Grupo</th>
                                    <th class="text-right">Arancel</th>
                                    <th class="text-center">Visible</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="product in filteredProducts" :key="product.id" :class="{ 'row-hidden': !product.visible }">
                                    <td>
                                        <div class="program-name">{{ product.nombre }}</div>
                                        <small class="text-muted">ID {{ product.hubspot_product_id }}</small>
                                    </td>
                                    <td>
                                        <span class="source-type">{{ product.tipo_programa_hubspot || "Sin clasificación" }}</span>
                                    </td>
                                    <td>
                                        <select
                                            class="form-control form-control-sm group-select"
                                            :value="product.grupo"
                                            :disabled="isSaving(product)"
                                            @change="changeGroup(product, $event)"
                                        >
                                            <option v-for="group in groups" :key="group.key" :value="group.key">{{ group.label }}</option>
                                        </select>
                                    </td>
                                    <td class="text-right"><strong class="price">{{ formatCurrency(product.arancel) }}</strong></td>
                                    <td class="text-center">
                                        <button
                                            type="button"
                                            :class="['visibility-toggle', { enabled: product.visible }]"
                                            :disabled="isSaving(product)"
                                            :title="product.visible ? 'Ocultar del simulador' : 'Mostrar en el simulador'"
                                            @click="toggleVisibility(product)"
                                        >
                                            <span class="toggle-track"><span class="toggle-knob"></span></span>
                                            <span>{{ product.visible ? "Visible" : "Oculto" }}</span>
                                            <i v-if="isSaving(product)" class="bx bx-loader-alt bx-spin"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<style scoped>
.aranceles-page { padding-bottom: 3rem; }
.hero-card {
    background: linear-gradient(125deg, #102a56 0%, #174a8b 58%, #1b6ca8 100%);
    border-radius: 18px;
    color: #fff;
    padding: 28px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 30px;
    box-shadow: 0 12px 28px rgba(16, 42, 86, .2);
}
.hero-card h2 { color: #fff; margin: 4px 0 8px; font-size: 28px; }
.hero-card p { color: rgba(255,255,255,.78); max-width: 650px; }
.eyebrow { text-transform: uppercase; font-size: 11px; font-weight: 700; letter-spacing: 1.4px; color: #8bc7ff; }
.sync-area { min-width: 290px; display: flex; flex-direction: column; align-items: flex-end; gap: 5px; }
.sync-area small { color: rgba(255,255,255,.65); }
.sync-area strong { margin-bottom: 8px; }
.sync-area .btn { color: #174a8b; font-size: 14px; font-weight: 600; border-radius: 10px; }
.sync-area .btn i { font-size: 20px; vertical-align: middle; margin-right: 4px; }
.metric-card { background: #fff; border-radius: 14px; padding: 20px; display: flex; align-items: center; gap: 15px; box-shadow: 0 3px 15px rgba(30, 50, 80, .07); height: 100%; }
.metric-icon { width: 48px; height: 48px; border-radius: 12px; display: grid; place-items: center; font-size: 25px; }
.metric-card span { display: block; color: #74788d; font-size: 13px; }
.metric-card strong { display: block; color: #253858; font-size: 25px; line-height: 1.2; }
.catalog-card { border: 0; border-radius: 16px; box-shadow: 0 4px 18px rgba(30, 50, 80, .08); }
.catalog-card .card-body { padding: 24px; }
.group-tabs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; background: #f4f6f9; border-radius: 13px; padding: 6px; }
.group-tab { border: 0; background: transparent; color: #65738b; border-radius: 9px; padding: 12px; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 8px; transition: all .2s; }
.group-tab i { font-size: 20px; }
.group-tab.active { color: #174a8b; background: #fff; box-shadow: 0 3px 10px rgba(30, 50, 80, .1); }
.search-box { position: relative; }
.search-box i { position: absolute; left: 14px; top: 11px; color: #8997aa; font-size: 19px; }
.search-box .form-control { padding-left: 42px; }
.table thead th { border-top: 0; border-bottom: 1px solid #e7ebf1; color: #718096; text-transform: uppercase; font-size: 11px; letter-spacing: .5px; padding: 13px 12px; }
.table td { vertical-align: middle; padding: 15px 12px; border-color: #eef1f5; }
.program-name { color: #253858; font-weight: 600; max-width: 430px; }
.source-type { display: inline-block; background: #eef4fb; color: #416386; padding: 5px 9px; border-radius: 7px; font-size: 12px; }
.group-select { min-width: 120px; border-color: #dce3ec; }
.price { color: #174a8b; white-space: nowrap; }
.row-hidden { background: #fafbfc; }
.row-hidden .program-name, .row-hidden .price { color: #8c98a8; }
.visibility-toggle { border: 0; background: transparent; color: #8994a5; display: inline-flex; align-items: center; justify-content: center; gap: 7px; font-size: 12px; min-width: 105px; }
.visibility-toggle.enabled { color: #27a36a; font-weight: 600; }
.toggle-track { width: 34px; height: 19px; background: #c8d0db; border-radius: 10px; padding: 2px; transition: background .2s; }
.toggle-knob { display: block; width: 15px; height: 15px; background: #fff; border-radius: 50%; box-shadow: 0 1px 3px rgba(0,0,0,.2); transition: transform .2s; }
.visibility-toggle.enabled .toggle-track { background: #35b87f; }
.visibility-toggle.enabled .toggle-knob { transform: translateX(15px); }
.empty-state { text-align: center; padding: 60px 20px; color: #7f8b9c; }
.empty-state i { display: block; font-size: 42px; color: #9db0c7; margin-bottom: 10px; }
.empty-state.compact { padding: 35px 20px; }
@media (max-width: 767px) {
    .hero-card { flex-direction: column; align-items: stretch; padding: 24px; }
    .sync-area { min-width: 0; align-items: stretch; }
    .group-tabs { grid-template-columns: 1fr; }
    .group-tab { justify-content: flex-start; }
}
</style>
